import { OpenInNew } from '@mui/icons-material'
import { Box, Button, Stack, Typography, type SxProps, type Theme } from '@mui/material'
import { useState } from 'react'
import { toast } from 'react-toastify'
import { PlanOrganizationTaskStatus } from '../../../models/PlanOrganizationTask'
import { UpdatePlanOrganizationTaskStatus } from '../../../services/AnnualPlanService'
import { StyledToggle, type ToggleOption } from './StyledToggle'

interface Props {
  planOrganizationTaskId: number
  completed: boolean
  title: string
  dueDate: Date
  instructionsUrl: string
  color: string
  successMessage: string
  dataTestId: string
}

export const DataTool = (props: Props): JSX.Element => {
  const [isCompleted, setIsCompleted] = useState<string>(String(props.completed))

  const styles: Record<string, SxProps<Theme>> = {
    container: {
      width: '100%',
      bgcolor: props.color,
      borderRadius: '12px',
      overflow: 'hidden'
    },
    box: {
      bgcolor: 'var(--white)',
      p: '20px',
      border: `5px solid ${props.color}`,
      borderTop: 0,
      borderBottomLeftRadius: '12px',
      borderBottomRightRadius: '12px'
    },
    button: {
      width: 'fit-content',
      height: 'fit-content',
      padding: '9.5px 20px',
      bgcolor: props.color,
      color: 'var(--white)',
      textTransform: 'none',
      fontSize: '16px',
      fontWeight: '600',
      gap: '36px',
      mt: 'auto'
    }
  }

  const toggleOptions: Array<ToggleOption<string>> = [
    { value: 'false', label: 'Incomplete' },
    { value: 'true', label: 'Complete' }
  ]

  const handleAccessInstructions = (): void => {
    window.open(props.instructionsUrl, '_blank', 'noopener,noreferrer')
  }

  const handleChange = async (value: string): Promise<void> => {
    const newStatus = value === 'true' ? PlanOrganizationTaskStatus.UpdatesSubmitted : PlanOrganizationTaskStatus.NotStarted
    const { success, errors } = await UpdatePlanOrganizationTaskStatus(props.planOrganizationTaskId, newStatus)
    if (!success) {
      for (const error of errors) {
        toast.error(error)
      }
      return
    }
    setIsCompleted(value)
    toast.success(props.successMessage)
  }

  const formattedDueDate = new Date(props.dueDate).toLocaleDateString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  })

  return (
    <Stack
      data-testid={props.dataTestId}
      sx={styles.container}
    >
      <Box sx={{ padding: '16px' }}>
        <Typography variant='h3' color='var(--white)'>{props.title}</Typography>
      </Box>
      <Stack gap='20px' sx={styles.box}>
        <Typography variant='body1' fontWeight='600'>
          This tool must be completed outside of the START PDS. Please access the instructions linked below. Once finished, mark it as complete here by the due date: {formattedDueDate}.
        </Typography>

        <Stack direction='row' gap='20px'>
          <Button sx={styles.button} variant='contained' onClick={handleAccessInstructions}>
            Access Instructions
            <OpenInNew color='inherit' />
          </Button>

          <StyledToggle
            options={toggleOptions}
            selectedValue={isCompleted}
            onSelectionChange={handleChange}
            backgroundColor={props.color}
            dataTestId={`${props.dataTestId}Toggle`}
          />
        </Stack>
      </Stack>
    </Stack>
  )
}
